import { useState, useMemo } from 'react';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useTemplates, useApplyTemplate } from '@/hooks/schedule';
import { useCompanyProfiles } from '@/hooks/schedule/useCompanyProfiles';
import { useAuth } from '@/hooks/useAuth';

interface ApplyTemplateDialogProps {
  open: boolean;
  year: number;
  month: number;
  onClose: () => void;
  onSuccess: () => void;
}

export const ApplyTemplateDialog = ({
  open,
  year,
  month,
  onClose,
  onSuccess,
}: ApplyTemplateDialogProps) => {
  const { profile } = useAuth();
  const { data: templates } = useTemplates(profile?.company_id);
  const { data: profiles } = useCompanyProfiles(profile?.company_id || '');
  const { mutate: applyTemplate, isPending } = useApplyTemplate();

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [selectedProfileIds, setSelectedProfileIds] = useState<Set<string>>(new Set());
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter profiles by search query
  const filteredProfiles = useMemo(() => {
    if (!profiles) return [];
    if (!searchQuery.trim()) return profiles;
    
    const query = searchQuery.toLowerCase();
    return profiles.filter(
      (p) =>
        p.first_name?.toLowerCase().includes(query) ||
        p.last_name?.toLowerCase().includes(query) ||
        p.email?.toLowerCase().includes(query)
    );
  }, [profiles, searchQuery]);

  const handleProfileToggle = (profileId: string) => {
    const newSelected = new Set(selectedProfileIds);
    if (newSelected.has(profileId)) {
      newSelected.delete(profileId);
    } else {
      newSelected.add(profileId);
    }
    setSelectedProfileIds(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedProfileIds.size === filteredProfiles.length) {
      setSelectedProfileIds(new Set());
    } else {
      setSelectedProfileIds(new Set(filteredProfiles.map((p) => p.id)));
    }
  };

  const handleApply = () => {
    if (!selectedTemplateId || selectedProfileIds.size === 0) return;

    applyTemplate(
      {
        template_id: selectedTemplateId,
        profile_ids: Array.from(selectedProfileIds),
        year,
        month,
        overwrite_existing: overwriteExisting,
      },
      {
        onSuccess: () => {
          onSuccess();
          handleClose();
        },
      }
    );
  };

  const handleClose = () => {
    setSelectedTemplateId('');
    setSelectedProfileIds(new Set());
    setOverwriteExisting(false);
    setSearchQuery('');
    onClose();
  };

  const canApply = selectedTemplateId && selectedProfileIds.size > 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Apply Template to Roster</DialogTitle>
          <DialogDescription>
            Select a template and employees to apply it to {new Date(year, month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Template Selection */}
          <div className="space-y-2">
            <Label>Select Template</Label>
            <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a template" />
              </SelectTrigger>
              <SelectContent>
                {templates?.map((template) => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.name} ({template.num_weeks}{' '}
                    {template.num_weeks === 1 ? 'week' : 'weeks'})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Employee Selection */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Select Employees ({selectedProfileIds.size} selected)</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleSelectAll}
                disabled={filteredProfiles.length === 0}
              >
                {selectedProfileIds.size === filteredProfiles.length ? 'Deselect All' : 'Select All'}
              </Button>
            </div>

            <Input
              placeholder="Search employees..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            <ScrollArea className="h-[300px] border rounded-md p-4">
              <div className="space-y-3">
                {filteredProfiles.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No employees found
                  </p>
                ) : (
                  filteredProfiles.map((p) => (
                    <div key={p.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`profile-${p.id}`}
                        checked={selectedProfileIds.has(p.id)}
                        onCheckedChange={() => handleProfileToggle(p.id)}
                      />
                      <label
                        htmlFor={`profile-${p.id}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex-1"
                      >
                        {p.first_name} {p.last_name}
                        <span className="text-muted-foreground ml-2 text-xs">
                          ({p.role})
                        </span>
                      </label>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Options */}
          <div className="space-y-3 pt-2 border-t">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="overwrite"
                checked={overwriteExisting}
                onCheckedChange={(checked) => setOverwriteExisting(checked as boolean)}
              />
              <label
                htmlFor="overwrite"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                Overwrite existing shifts
              </label>
            </div>
            <p className="text-xs text-muted-foreground ml-6">
              If unchecked, template will only fill empty cells
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={!canApply || isPending}>
            <Check className="h-4 w-4 mr-2" />
            {isPending ? 'Applying...' : `Apply to ${selectedProfileIds.size} Employee${selectedProfileIds.size !== 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
